/**
 * Modale affichée quand l'utilisateur tente de créer une fiche sans crédit
 * disponible. Déclenche la redirection vers `/pricing` pour l'upsell.
 *
 * 4 variants selon `gatingReason` (cf. src/lib/billing.ts) :
 *   - `no-subscription-no-credits` — nouveau user, pas de crédit gratuit actif
 *   - `free-credit-expired`        — crédit gratuit expiré (7j passés)
 *   - `monthly-quota-exhausted`    — abonné, quota mensuel consommé
 *   - `subscription-past-due`      — paiement Stripe en échec
 *   - `subscription-canceled`      — abonnement résilié
 *
 * Le CTA principal mène vers `/pricing` (ancre appropriée : #credits pour les
 * abonnés quota-exhausted, top de page sinon).
 */

import { Button, Card, Pill } from "./ui";
import { Ic } from "./icons";
import type { GatingReason } from "../lib/billing";

type Props = {
  reason: GatingReason;
  onClose: () => void;
  onGoToPricing: (anchor?: string) => void;
};

const COPY: Record<
  NonNullable<GatingReason>,
  {
    title: string;
    body: string;
    primaryLabel: string;
    primaryAnchor?: string;
    tone: "amber" | "red" | "lime";
  }
> = {
  "no-subscription-no-credits": {
    title: "Commencez avec une fiche offerte",
    body: "Créez un compte pour recevoir 1 fiche gratuite, valable 7 jours. Au-delà, choisissez un abonnement ou un pack ponctuel.",
    primaryLabel: "Voir les plans",
    tone: "lime",
  },
  "free-credit-expired": {
    title: "Votre fiche gratuite a expiré",
    body: "Les 7 jours d'essai sont écoulés. Passez à un plan à partir de 44 €/mois ou achetez un pack ponctuel pour continuer.",
    primaryLabel: "Voir les plans",
    tone: "amber",
  },
  "monthly-quota-exhausted": {
    title: "Quota mensuel atteint",
    body: "Vous avez utilisé toutes les fiches incluses dans votre plan ce mois-ci. Ajoutez des crédits à la volée (auto-facturés) ou passez au plan supérieur.",
    primaryLabel: "Acheter des crédits add-on",
    primaryAnchor: "credits",
    tone: "amber",
  },
  "subscription-past-due": {
    title: "Paiement en échec",
    body: "Nous n'avons pas pu prélever votre dernière échéance. Vérifiez votre moyen de paiement dans Paramètres → Facturation.",
    primaryLabel: "Voir la facturation",
    tone: "red",
  },
  "subscription-canceled": {
    title: "Abonnement résilié",
    body: "Votre abonnement est clôturé. Reprenez un plan pour générer de nouvelles fiches — vos fiches existantes restent accessibles.",
    primaryLabel: "Reprendre un plan",
    tone: "red",
  },
};

export function CreditsExhaustedModal({ reason, onClose, onGoToPricing }: Props) {
  if (!reason) return null;
  const copy = COPY[reason];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="credits-modal-title"
      className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <Card
        className="max-w-[440px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Pill tone={copy.tone} icon={<Ic.Bolt size={12} />}>
            Crédit requis
          </Pill>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
            aria-label="Fermer"
          >
            <Ic.Cross size={14} />
          </button>
        </div>
        <h3
          id="credits-modal-title"
          className="mt-3 text-[20px] font-semibold tracking-tight"
        >
          {copy.title}
        </h3>
        <p className="mt-2 text-[13.5px] text-ink-700 leading-relaxed">
          {copy.body}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <Button
            variant="dark"
            className="w-full"
            onClick={() => onGoToPricing(copy.primaryAnchor)}
            iconRight={<Ic.Arrow size={14} />}
          >
            {copy.primaryLabel}
          </Button>
          <Button variant="soft" className="w-full" onClick={onClose}>
            Plus tard
          </Button>
        </div>
      </Card>
    </div>
  );
}
